import { ArrowLeft } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import BalanceDetails from '@/components-latest/ui/BillingPage/WalletTransactions/BalanceDetails'
import RequestPayoutModal from '@/components-latest/ui/BillingPage/WalletTransactions/RequestPayoutModal'
import {
  DEFAULT_WALLET_TRANSACTIONS_PAGE_SIZE,
  WalletTransactionsPageSize
} from '@/components-latest/ui/BillingPage/WalletTransactions/constants'
import TransactionDetailsDrawer from '@/components-latest/ui/BillingPage/WalletTransactions/TransactionDetailsDrawer'
import TransactionsList from '@/components-latest/ui/BillingPage/WalletTransactions/TransactionsList'
import { useLazyFetchWalletTransactionDetailsQuery } from '@/ducks/billing/api/billing.api'
import { resolveFundingAttemptIdForWalletTransaction } from '@/ducks/billing/billing.wallet-funding-links'
import { StoreFundingMethodsResponse } from '@/ducks/billing/billing.wallet-funding.types'

import {
  hasActiveWalletTransactionsFilters,
  type WalletTransactionsDateRangePreset
} from '@/ducks/billing/billing.wallet-transactions.filters.helpers'

import { WalletTransaction } from './types'
import Styles from './WalletTransactions.module.scss'

type BrandFilterValue = 'kool' | 'yassir' | undefined

type Props = {
  onBack?: () => void
  transactions?: WalletTransaction[]
  settledBalance?: number
  pendingBalance?: number
  showPendingBalance?: boolean
  currencyCode?: string
  isLoading?: boolean
  formatDateTime?: (value: string) => string
  page?: number
  limit?: WalletTransactionsPageSize
  offset?: number
  total?: number
  onPageChange?: (page: number) => void
  onLimitChange?: (limit: WalletTransactionsPageSize) => void
  brandFilter?: BrandFilterValue
  onBrandFilterChange?: (brand: BrandFilterValue) => void
  dateRange?: WalletTransactionsDateRangePreset
  dateFrom?: string
  dateTo?: string
  onDatePresetChange?: (preset: WalletTransactionsDateRangePreset | undefined) => void
  onCustomDateRangeChange?: (dateFrom: string, dateTo: string) => void
  onClearDateFilter?: () => void
  onClearAllFilters?: () => void
  canRequestPayout?: boolean
  requestPayoutDisabledReason?: 'country' | 'generic' | null
  fundingMethods?: StoreFundingMethodsResponse | null
  isFundingMethodsLoading?: boolean
  fundingMethodsError?: unknown
  onPayoutSuccess?: (result?: {
    fundingAttemptId: string
    amount: number
    currency: string
  }) => void
}

const WalletTransactions = ({
  onBack,
  transactions = [],
  settledBalance = 0,
  pendingBalance = 0,
  showPendingBalance = false,
  currencyCode = 'DA',
  isLoading = false,
  formatDateTime,
  page = 0,
  limit = DEFAULT_WALLET_TRANSACTIONS_PAGE_SIZE,
  offset = 0,
  total,
  onPageChange,
  onLimitChange,
  brandFilter,
  onBrandFilterChange,
  dateRange,
  dateFrom,
  dateTo,
  onDatePresetChange,
  onCustomDateRangeChange,
  onClearDateFilter,
  onClearAllFilters,
  canRequestPayout = false,
  requestPayoutDisabledReason = null,
  fundingMethods,
  isFundingMethodsLoading = false,
  fundingMethodsError,
  onPayoutSuccess
}: Props) => {
  const { t } = useTranslation()
  const hasActiveFilters = hasActiveWalletTransactionsFilters({
    brand: brandFilter,
    dateRange,
    dateFrom,
    dateTo
  })
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false)
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null)
  const [selectedFundingAttemptId, setSelectedFundingAttemptId] = useState<
    string | undefined
  >(undefined)
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false)

  const [
    getWalletTransactionDetails,
    {
      data: walletTransactionDetails,
      isFetching: isWalletTransactionDetailsFetching,
      isError: isWalletTransactionDetailsError
    }
  ] = useLazyFetchWalletTransactionDetailsQuery()

  const activeWalletTransactionDetails =
    selectedTransactionId &&
    walletTransactionDetails?.transaction.transactionId === selectedTransactionId
      ? walletTransactionDetails
      : undefined

  const isWalletTransactionDetailsLoading =
    Boolean(selectedTransactionId) &&
    !isWalletTransactionDetailsError &&
    (isWalletTransactionDetailsFetching ||
      walletTransactionDetails?.transaction.transactionId !== selectedTransactionId)

  const resolveDateTime = useCallback(
    (value: string) => {
      if (formatDateTime) return formatDateTime(value)
      if (!value) return '-'
      return value
    },
    [formatDateTime]
  )

  useEffect(() => {
    if (!isDetailsDrawerOpen || !selectedTransactionId) return
    getWalletTransactionDetails({ transactionId: selectedTransactionId })
  }, [
    getWalletTransactionDetails,
    isDetailsDrawerOpen,
    selectedTransactionId
  ])

  const effectiveFundingAttemptId =
    selectedFundingAttemptId ||
    activeWalletTransactionDetails?.transaction.fundingAttemptId

  const selectedListTransaction = transactions.find(
    transaction => transaction.id === selectedTransactionId
  )

  const handleViewDetails = useCallback(
    (transactionId: string, fundingAttemptId?: string) => {
      const transaction = transactions.find(item => item.id === transactionId)
      const resolvedFundingAttemptId =
        fundingAttemptId ||
        transaction?.fundingAttemptId ||
        (transaction
          ? resolveFundingAttemptIdForWalletTransaction({
              transactionId: transaction.id,
              type: transaction.type,
              status: transaction.status,
              amount: transaction.amount
            })
          : undefined)

      setSelectedTransactionId(transactionId)
      setSelectedFundingAttemptId(resolvedFundingAttemptId)
      setIsDetailsDrawerOpen(true)
    },
    [transactions]
  )

  const handleDetailsDrawerOpenChange = useCallback((open: boolean) => {
    setIsDetailsDrawerOpen(open)
    if (!open) {
      setSelectedTransactionId(null)
      setSelectedFundingAttemptId(undefined)
    }
  }, [])

  const balanceDetails = (
    <BalanceDetails
      settledBalance={settledBalance}
      pendingBalance={pendingBalance}
      showPendingBalance={showPendingBalance}
      currency={currencyCode}
      canRequestPayout={canRequestPayout}
      requestPayoutDisabledReason={requestPayoutDisabledReason}
      onRequestPayout={() => setIsPayoutModalOpen(true)}
    />
  )

  return (
    <div className={Styles.card} data-testid="wallet-transactions">
      <div className={Styles.header}>
        <div className={Styles.actions}>
          <div className={Styles.leftHeader}>
            <button
              type="button"
              className={Styles.backButton}
              onClick={onBack}
              aria-label={t('common.cta.back')}>
              <ArrowLeft width={18} height={14} />
              <Text
                variant="nextBody"
                weight="semiBold"
                className={Styles.backText}>
                {t('common.cta.back')}
              </Text>
            </button>
            <div className={Styles.headerDivider} />
            <Text variant="heading-05" weight="semiBold">
              {t('billing.wallet.page-title')}
            </Text>
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className={Styles.loadingWrapper}>
          <Spinner />
        </div>
      ) : (
        <>
          {balanceDetails}
          <TransactionsList
            transactions={transactions}
            page={page}
            limit={limit}
            offset={offset}
            total={total}
            onPageChange={onPageChange || (() => undefined)}
            onLimitChange={onLimitChange || (() => undefined)}
            onViewDetails={handleViewDetails}
            brandFilter={brandFilter}
            onBrandFilterChange={onBrandFilterChange}
            dateRange={dateRange}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDatePresetChange={onDatePresetChange}
            onCustomDateRangeChange={onCustomDateRangeChange}
            onClearDateFilter={onClearDateFilter}
            hasActiveFilters={hasActiveFilters}
            onClearAllFilters={onClearAllFilters}
          />
          <TransactionDetailsDrawer
            open={isDetailsDrawerOpen}
            onOpenChange={handleDetailsDrawerOpenChange}
            details={activeWalletTransactionDetails}
            listTransaction={selectedListTransaction}
            fundingAttemptId={effectiveFundingAttemptId}
            isLoading={isWalletTransactionDetailsLoading}
            isError={isWalletTransactionDetailsError}
            formatDateTime={resolveDateTime}
          />
        </>
      )}
      <RequestPayoutModal
        open={isPayoutModalOpen}
        onOpenChange={setIsPayoutModalOpen}
        balanceAmount={settledBalance}
        currency={currencyCode}
        fundingMethods={fundingMethods}
        isFundingMethodsLoading={isFundingMethodsLoading}
        fundingMethodsError={fundingMethodsError}
        onSuccess={onPayoutSuccess}
      />
    </div>
  )
}

export default WalletTransactions
